import { useEffect, useState } from "react";
import { Button, Grid, MenuItem, styled, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import {
  getDefaultCutoffLowerLimit,
  getMostRecentNonFutureRunYear,
  sortCutoffsByLowerLimit,
  useRunYears,
} from "src/hooks/runYearHooks";
import { useSrids } from "src/hooks/useSrids";
import { useCaseCreateSchema } from "src/schemas/case-create.schema";
import { CaseCreationType } from "src/types";

export interface CaseCreateFormValues {
  name: string;
  description: string;
  userSrid: string;
  caseType: CaseCreationType;
  runYear: number | null;
  cutoff: number | null;
}

interface Props {
  values: CaseCreateFormValues | null;
  onCancel: () => void;
  onSubmit: (values: CaseCreateFormValues) => void;
}

export function CaseCreateForm(props: Props) {
  const { values } = props;

  const { t } = useTranslation();
  const { data: srids } = useSrids();
  const validationSchema = useCaseCreateSchema();
  const { data: runYears } = useRunYears();
  const fallbackRunYearInfo = getMostRecentNonFutureRunYear(runYears);
  const fallbackRunYear = fallbackRunYearInfo?.year;
  const fallbackCutoff = getDefaultCutoffLowerLimit(
    fallbackRunYearInfo?.cutoffList
  );

  const [initialValues, setInitialValues] = useState<CaseCreateFormValues>({
    name: values?.name || "",
    description: values?.description || "",
    caseType: values?.caseType || CaseCreationType.PREDEFINED,
    userSrid: values?.userSrid || "",
    runYear: values?.runYear || fallbackRunYear || null,
    cutoff: values?.cutoff || fallbackCutoff || null,
  });

  /**
   * Effects
   */
  useEffect(setInitialSrid, [srids]);

  /**
   * Functions
   */
  function setInitialSrid() {
    if (srids) {
      setInitialValues((v) => ({ ...v, userSrid: srids[0].toString() }));
    }
  }

  return render();

  function render() {
    if (!srids) return null;

    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: CaseCreateFormValues) => {
          props.onSubmit(values);
        }}
        enableReinitialize
      >
        {({ values, setFieldValue }) => {
          const availableCutoffs = sortCutoffsByLowerLimit(
            runYears?.find((runYear) => runYear.year === values.runYear)
              ?.cutoffList || [],
          );
          return (
            <Styles>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h4" color="primary">
                    {t("title.createCase")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={TextField}
                    name="name"
                    label={t("label.name")}
                    type="text"
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    component={TextField}
                    name="description"
                    label={t("label.description")}
                    multiline
                    rows={5}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    component={TextField}
                    type="text"
                    name="userSrid"
                    select={true}
                    fullWidth
                    variant="outlined"
                    label={t("label.srid")}
                  >
                    {srids.map((srid) => (
                      <MenuItem key={srid} value={srid}>
                        EPSG:{srid}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                {(runYears?.length || 0) > 1 && (
                  <Grid item xs={12}>
                    <Field
                      component={TextField}
                      select={true}
                      type="number"
                      name="runYear"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const selectedRunYear = +e.target.value;
                        const availableCutoffs = sortCutoffsByLowerLimit(
                          runYears?.find(
                            (runYear) => runYear.year === selectedRunYear,
                          )?.cutoffList || [],
                        );
                        setFieldValue("runYear", selectedRunYear);
                        setFieldValue(
                          "cutoff",
                          getDefaultCutoffLowerLimit(availableCutoffs) ?? null
                        );
                      }}
                      label={t("label.runYear")}
                      variant="outlined"
                      fullWidth
                    >
                      {runYears?.map((runYear) => (
                        <MenuItem key={runYear.year} value={runYear.year}>
                          {runYear.year}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                )}

                {(availableCutoffs.length || 0) > 1 && (
                  <Grid item xs={12}>
                    <Field
                      component={TextField}
                      select={true}
                      type="number"
                      name="cutoff"
                      label={t("label.trafficNetwork")}
                      variant="outlined"
                      fullWidth
                    >
                      {availableCutoffs.map((cutoff) => (
                        <MenuItem
                          key={cutoff.lowerLimit}
                          value={cutoff.lowerLimit}
                        >
                          {cutoff.translation}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                )}

                <ButtonGrid
                  container
                  item
                  xs={12}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <Button variant="text" onClick={props.onCancel}>
                    {t("label.cancel")}
                  </Button>
                  <Button type="submit" color="primary" variant="contained">
                    {t("label.next")}
                  </Button>
                </ButtonGrid>
              </Grid>
            </Styles>
          );
        }}
      </Formik>
    );
  }
}

const Styles = styled(Form)`
  .MuiFormControl-root {
    width: 100%;
  }
`;
